import {
  proposeTellOClear,
  proposeTellODepth,
  proposeTellOKeep,
  proposeTellOLikeBase,
  proposeTellOLoaderMode,
  proposeTellOProducts,
  proposeTellOShelf,
  proposeTellOShelfPosition,
  proposeTellOWidth,
} from "./build";
import { tellOFacts, tellOLoaderEvidence, type TellOContext } from "./facts";
import { tellOLengthToX100, type TellOTypedUnit } from "./lengths";
import type { RuleProposal, TellOFeature, TellOUnit } from "./proposal";

/** Lower case, plain quotes, no politeness around the ask, no closing punctuation. */
export const normalizeTellOText = (text: string): string => {
  let normalized = text
    .toLowerCase()
    .replace(/[’‘`]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/\s+/g, " ")
    .trim()
    .replace(/[.!?,;:]+$/, "")
    .trim();
  const politeStart =
    /^(?:please|ok|okay|hey o|o|can you|could you|would you|i want to|i'd like to|i would like to|let's|lets)[\s,]+/;
  const politeEnd = /[\s,]+(?:please|thanks|thank you)$/;
  for (let pass = 0; pass < 3; pass += 1) {
    normalized = normalized.replace(politeStart, "").replace(politeEnd, "").trim();
  }
  return normalized;
};

const WORD_NUMBERS: Record<string, number> = {
  half: 0.5,
  quarter: 0.25,
  "three quarters": 0.75,
  "three quarter": 0.75,
  "three-quarters": 0.75,
  "three-quarter": 0.75,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
};

const LENGTH_PATTERN =
  /(?:\b(?:an?)\s+)?(?:([-−])\s*)?(\d+\s*\/\s*\d+|\d*\.\d+|\d+|\bthree[\s-]quarters?\b|\bhalf\b|\bquarter\b|\b(?:one|two|three|four|five|six|seven|eight|nine|ten)\b)(?:\s+(?:of\s+)?an?\b)?\s*(inches\b|inch\b|in\b|"|centimet(?:er|re)s?\b|cm\b|millimet(?:er|re)s?\b|mm\b)?/g;

const typedUnitOf = (unit: string | undefined): TellOTypedUnit | null => {
  if (!unit) return null;
  if (unit === '"' || unit.startsWith("in")) return "in";
  if (unit.startsWith("c")) return "cm";
  return "mm";
};

const numberOf = (raw: string): number => {
  const word = WORD_NUMBERS[raw.replace(/\s+/g, " ")];
  if (word !== undefined) return word;
  const fraction = raw.match(/^(\d+)\s*\/\s*(\d+)$/);
  if (fraction) return Number(fraction[1]) / Number(fraction[2]);
  return Number(raw);
};

export interface TellOLength {
  /** In the window's unit, x100; negative when typed with a minus. */
  x100: number;
  typedUnit: TellOTypedUnit | null;
  /** The span of normalized text it was read from. */
  start: number;
  end: number;
}

/** The one length in a normalized text; null for none, or for two (a size is never guessed). */
export const parseTellOLength = (text: string, unit: TellOUnit): TellOLength | null => {
  const found: TellOLength[] = [];
  for (const match of text.matchAll(LENGTH_PATTERN)) {
    const start = match.index ?? 0;
    // A dash after a word or number is a range or a hyphen, not a minus.
    const sign = match[1] && !/[\w.]/.test(text[start - 1] ?? "") ? -1 : 1;
    const raw = match[2];
    const typed = match[3];
    const isDigits = /\d/.test(raw);
    const isFraction = /half|quarter/.test(raw);
    if (!isDigits && !isFraction && !typed) continue;
    const value = numberOf(raw);
    if (!Number.isFinite(value) || value <= 0) continue;
    found.push({
      x100: sign * tellOLengthToX100(value, typedUnitOf(typed), unit),
      typedUnit: typedUnitOf(typed),
      start,
      end: start + match[0].trimEnd().length,
    });
  }
  return found.length === 1 ? found[0] : null;
};

export interface TellOShelfReference {
  bayNo: number;
  shelfNo: number;
  storeCode: string | null;
  everyStore: boolean;
}

const SHELF_PATTERNS: { pattern: RegExp; bay: number; shelf: number }[] = [
  {
    pattern:
      /\bbay\s*(?:#|no\.?|number)?\s*(\d+)\s*(?:,|and|\/|-)?\s*shelf\s*(?:#|no\.?|number)?\s*(\d+)\b/,
    bay: 1,
    shelf: 2,
  },
  {
    pattern: /\bshelf\s*(?:#|no\.?|number)?\s*(\d+)\s*(?:of|on|in)\s*(?:the\s*)?bay\s*(\d+)\b/,
    bay: 2,
    shelf: 1,
  },
];
const STORE_PATTERN = /\b(?:in|at|for)?\s*store\s*(?:#|no\.?|number|code)?\s*([a-z0-9-]*\d[a-z0-9-]*)\b/;
const EVERY_STORE_PATTERN =
  /\b(?:in\s+|at\s+|for\s+)?(?:every|all|each|any)\s+(?:of\s+the\s+|the\s+)?stores?\b|\beverywhere\b/;

/** "bay 3 shelf 2" / "shelf 2 of bay 3", with "in store 1023" or "in every store". */
export const parseTellOShelfReference = (text: string): TellOShelfReference | null => {
  for (const { pattern, bay, shelf } of SHELF_PATTERNS) {
    const match = text.match(pattern);
    if (!match) continue;
    const store = text.match(STORE_PATTERN);
    return {
      bayNo: Number(match[bay]),
      shelfNo: Number(match[shelf]),
      storeCode: store ? store[1].toUpperCase() : null,
      everyStore: EVERY_STORE_PATTERN.test(text),
    };
  }
  return null;
};

const POSITION_PATTERN = /\b(top|bottom|highest|lowest)\s+shel(?:f|ves)\b|\bshelf\s*(\d+)\b/;

/** "the top shelf", "bottom shelves", "shelf 2", when no bay is named. */
export const parseTellOShelfPosition = (text: string): "top" | "bottom" | number | null => {
  if (/\bbay\b/.test(text)) return null;
  const match = text.match(POSITION_PATTERN);
  if (!match) return null;
  if (match[2]) return Number(match[2]);
  return match[1] === "top" || match[1] === "highest" ? "top" : "bottom";
};

const DIVIDER_WORD = /\bdividers?\b/;
const LOADER_WORD = /\b(?:spring\s+)?(?:loaders?|pushers?)\b|\bsprings?\b/;

/** Which feature the words name; empty when neither. */
export const tellOFeaturesNamed = (text: string): TellOFeature[] => [
  ...(DIVIDER_WORD.test(text) ? (["dividers"] as const) : []),
  ...(LOADER_WORD.test(text) ? (["loaders"] as const) : []),
];

const words = (text: string) => text.match(/[a-z0-9']+/g) ?? [];

/** True when nothing but these words is left: anything else is for the model. */
const onlyWords = (text: string, allowed: ReadonlySet<string>) =>
  words(text).every((word) => allowed.has(word));

const wordSet = (list: string) => new Set(list.split(/\s+/).filter(Boolean));

const FEATURE_WORDS = "divider dividers spring springs loader loaders pusher pushers";
const SIZE_WORDS = wordSet(
  `${FEATURE_WORDS} make set change the a an to be should use please wide thick deep width depth instead of it them all their my i want would like size sized each every so`,
);
const SHELF_WORDS = wordSet(
  `${FEATURE_WORDS} no not don't dont do never without remove take off away put add always use have has get gets give on at in for the a an let follow follows rule rule's again back to set by hand make keep please any one there here`,
);
const POSITION_WORDS = wordSet(
  `${FEATURE_WORDS} only just on the in at every all each any store stores put give get gets please for products of should go`,
);

const UNDO_PATTERN =
  /^(?:undo(?: that| it| this)?|reset(?: it| all| everything| the rule| the dividers)?|back to (?:the )?base(?: planogram)?|(?:go )?back to o'?s default|(?:go )?back to (?:the )?default|(?:use )?o'?s default|start over)$/;

const CLEAR_VERB =
  "(?:no|remove(?: all)?(?: the)?|clear(?: all)?(?: the)?|drop(?: the)?|without|turn off(?: the)?|take away(?: the)?|take off(?: the)?|get rid of(?: the)?|(?:don'?t|do not) (?:use|want|put|add)(?: any)?(?: the)?)";
const CLEAR_TAIL = "(?: at all| anywhere| on any shel(?:f|ves)| on all shel(?:f|ves)| on every shelf)?";
const DIVIDERS = "dividers?";
const LOADERS = "(?:spring )?(?:loaders?|pushers?)";
const EVERY_PRODUCT =
  "(?:everything|every product|all(?: the)? products|all|every shelf|all(?: the)? shelves|everywhere)";
const KEEP_TAIL =
  "(?:the )?(?:store'?s? |existing |store )?(?:hardware|fixtures?(?: dividers)?|dividers(?: on the fixtures?)?|fixture dividers)";

const PHRASES: { pattern: RegExp; propose: (context: TellOContext) => RuleProposal }[] = [
  {
    pattern: new RegExp(`^(?:${CLEAR_VERB} ${DIVIDERS}${CLEAR_TAIL}|${DIVIDERS} off)$`),
    propose: (context) => proposeTellOClear(context, "dividers"),
  },
  {
    pattern: new RegExp(`^(?:${CLEAR_VERB} ${LOADERS}${CLEAR_TAIL}|${LOADERS} off)$`),
    propose: (context) => proposeTellOClear(context, "loaders"),
  },
  {
    pattern: new RegExp(`^keep ${KEEP_TAIL}$`),
    propose: () => proposeTellOKeep(true),
  },
  {
    pattern: new RegExp(`^(?:(?:don'?t|do not|stop) keep(?:ing)?|drop|forget) ${KEEP_TAIL}$`),
    propose: () => proposeTellOKeep(false),
  },
  {
    pattern: new RegExp(`^(?:${LOADERS} )?(?:stay|stays|keep|kept) with the shel(?:f|ves)$`),
    propose: () => proposeTellOLoaderMode("shelf"),
  },
  {
    pattern: new RegExp(`^(?:${LOADERS} )?follows? the products?$`),
    propose: () => proposeTellOLoaderMode("products"),
  },
  {
    pattern: new RegExp(
      `^(?:(?:put|add|give|use) )?${DIVIDERS} (?:(?:on|for|to|with|between) )?${EVERY_PRODUCT}$`,
    ),
    propose: (context) => proposeTellOProducts(context, { features: ["dividers"], op: "all" }),
  },
  {
    pattern: new RegExp(
      `^(?:(?:put|add|give|use) )?${LOADERS} (?:(?:on|for|to|with|behind) )?${EVERY_PRODUCT}$`,
    ),
    propose: (context) => proposeTellOProducts(context, { features: ["loaders"], op: "all" }),
  },
  {
    pattern: new RegExp(`^${DIVIDERS} like (?:the )?base(?: planogram)?$`),
    propose: (context) => proposeTellOLikeBase(context),
  },
];

const FOR_EVERY_PRODUCT = new RegExp(`\\b(?:for|on|to) ${EVERY_PRODUCT}\\b`);

const cut = (text: string, start: number, end: number) =>
  `${text.slice(0, start)} ${text.slice(end)}`.replace(/\s+/g, " ").trim();

const without = (text: string, pattern: RegExp) => text.replace(pattern, " ").replace(/\s+/g, " ").trim();

const shelfAsk = (
  context: TellOContext,
  text: string,
  reference: TellOShelfReference,
): RuleProposal | null => {
  let rest = text;
  for (const { pattern } of SHELF_PATTERNS) rest = without(rest, pattern);
  rest = without(without(rest, STORE_PATTERN), EVERY_STORE_PATTERN);
  const features = tellOFeaturesNamed(rest);
  if (features.length > 1) return null;
  const feature: TellOFeature = features[0] ?? "dividers";
  const length = feature === "loaders" ? parseTellOLength(rest, context.unit) : null;
  if (length && length.x100 < 0) return { kind: "cannot", reason: { code: "not-understood" } };
  if (length) rest = cut(rest, length.start, length.end);
  if (!onlyWords(rest, SHELF_WORDS)) return null;

  const follows = /\bfollows?\b|\bback to the rule\b/.test(rest);
  const negated =
    /\b(?:no|not|don't|dont|do not|never|without|remove|off|take away|none)\b/.test(rest);
  const where = { bayNo: reference.bayNo, shelfNo: reference.shelfNo };

  const setting = (() => {
    if (feature === "dividers") {
      return {
        feature: "dividers" as const,
        dividersSetByHand: follows ? null : negated ? ("never" as const) : ("always" as const),
      };
    }
    if (follows) return { feature: "loaders" as const, loaderDepth: null };
    if (negated) return { feature: "loaders" as const, loaderDepth: 0 };
    const depth =
      length?.x100 ??
      (context.policy.loaderDepth && context.policy.loaderDepth > 0
        ? context.policy.loaderDepth
        : tellOLoaderEvidence(context).typicalDepth);
    return depth ? { feature: "loaders" as const, loaderDepth: depth } : null;
  })();
  if (!setting) return null;

  if (reference.everyStore) {
    const nearest = proposeTellOShelf(context, { ...where, ...setting });
    return {
      kind: "cannot",
      reason: {
        code: "every-store-override",
        ...where,
        storeCode: nearest.kind === "shelf" ? nearest.storeCode : null,
      },
      ...(nearest.kind === "shelf" ? { nearest } : {}),
    };
  }
  return proposeTellOShelf(context, { ...where, storeCode: reference.storeCode, ...setting });
};

const sizeAsk = (context: TellOContext, text: string, length: TellOLength): RuleProposal | null => {
  let rest = cut(text, length.start, length.end);
  const forEveryProduct = FOR_EVERY_PRODUCT.test(rest);
  if (forEveryProduct) rest = without(rest, FOR_EVERY_PRODUCT);
  const features = tellOFeaturesNamed(rest);
  if (features.length !== 1 || !onlyWords(rest, SIZE_WORDS)) return null;
  if (features[0] === "dividers") {
    const width = proposeTellOWidth(context, length.x100);
    if (!forEveryProduct || width.kind !== "rule" || !width.patch.hardware) return width;
    return proposeTellOProducts(context, {
      features: ["dividers"],
      op: "all",
      hardware: width.patch.hardware,
    });
  }
  const depth = proposeTellODepth(context, length.x100);
  if (!forEveryProduct || depth.kind !== "rule") return depth;
  return proposeTellOProducts(context, {
    features: ["loaders"],
    op: "all",
    loaderDepth: length.x100,
  });
};

/** Stage 2 (B.1) without a model; null hands product references, exclusions and mixed asks on. */
export const parseTellOText = (context: TellOContext, raw: string): RuleProposal | null => {
  const text = normalizeTellOText(raw);
  if (!text) return null;
  if (UNDO_PATTERN.test(text)) return { kind: "undo" };
  if (/\bstay(?:s)? with the shel(?:f|ves)\b/.test(text) && /\bfollows? the products?\b/.test(text)) {
    return { kind: "cannot", reason: { code: "both-modes" } };
  }

  const reference = parseTellOShelfReference(text);
  if (reference) return shelfAsk(context, text, reference);

  const position = parseTellOShelfPosition(text);
  if (position !== null) {
    let rest = without(without(text, POSITION_PATTERN), EVERY_STORE_PATTERN);
    rest = without(rest, /\bbase planogram'?s?\b/);
    if (!onlyWords(rest, POSITION_WORDS)) return null;
    const features = tellOFeaturesNamed(rest);
    return proposeTellOShelfPosition(context, position, features.length > 0 ? features : ["dividers"]);
  }

  for (const { pattern, propose } of PHRASES) {
    if (pattern.test(text)) return propose(context);
  }

  const length = parseTellOLength(text, context.unit);
  if (length) return sizeAsk(context, text, length);
  return null;
};

/** The size in use, for prefill chips: the rule's own, else the base's, else 0.25 in / 0.6 cm. */
export const tellOTypicalWidth = (context: TellOContext) =>
  tellOFacts(context, context.policy).hardware?.width ?? (context.unit === "cm" ? 60 : 25);
